// ============================================================================
// client/pet-papier.ts — LES DIX COMPAGNONS EN PAPIER DÉCOUPÉ
// ----------------------------------------------------------------------------
// Portage des silhouettes validées dans /pages/refonte-d.html. La règle qui les
// gouverne : c'est la SILHOUETTE qui fait reconnaître l'animal à la taille où
// on le voit vraiment courir derrière le héros — jamais le détail interne. Deux
// ou trois découpes posées dessus font le charme, pas la reconnaissance.
//
// Comme le héros, ils étaient blittés depuis une feuille et sont désormais
// peints à la volée, avec le même cache borné : le papier coûte trop cher pour
// être redessiné à chaque image.
// ============================================================================

import { decoupe, papier, rgba } from './papier'
import type { PosePet } from './pet-allure'

type Ctx = CanvasRenderingContext2D

/* LES DIX COMPAGNONS. Une silhouette franche découpée aux ciseaux — pas une boîte :
   c'est la SILHOUETTE qui fait reconnaître l'animal à la taille où on le voit vraiment. */
/**
 * LES DESSINS D'ORIGINE.
 *
 * Depuis le 31/07 et le choix des treize, un SEUL y recourt encore : le canari, que Claude-Alain
 * a voulu garder tel quel. Les douze autres branches ne sont plus atteintes en jeu.
 * ⚠️ Elles restent volontairement : ce sont le point de comparaison de la planche et le filet si
 * une direction devait être retirée. Les supprimer ferait gagner 120 lignes et perdre la
 * possibilité de revenir en arrière sans archéologie git.
 */
function petLegacy(g: Ctx, cx: number, base: number, T: number, kind: string): void {
  const u = T / 26
  const X = (v: number): number => cx + v * u, Y = (v: number): number => base - v * u
  /* une découpe suivant un chemin quelconque */
  const boite = { x: cx - 24 * u, y: base - 28 * u, w: 48 * u, h: 34 * u }
  const piece = (col: string, motif: string, trace: () => void): void =>
    decoupe(g, () => { g.beginPath(); trace() }, col, motif, boite, 2.5 * u, 3 * u)
  /* un corps arrondi : le geste de base de tous les animaux */
  const ovale = (dx: number, dy: number, rx: number, ry: number, col: string, motif: string, rot = 0): void =>
    piece(col, motif, () => g.ellipse(X(dx), Y(dy), rx * u, ry * u, rot, 0, 7))
  const poly = (pts: number[][], col: string, motif: string): void => piece(col, motif, () => {
    pts.forEach((pt, i) => i ? g.lineTo(X(pt[0]!), Y(pt[1]!)) : g.moveTo(X(pt[0]!), Y(pt[1]!))); g.closePath() })
  const oeil = (dx: number, dy: number, r = 1.5): void => { g.fillStyle = '#1e1a24'; g.beginPath(); g.arc(X(dx), Y(dy), r * u, 0, 7); g.fill() }
  const brin = (pts: number[][], col: string, w: number): void => { g.strokeStyle = col; g.lineWidth = w * u; g.lineCap = 'round'; g.lineJoin = 'round'
    g.beginPath(); pts.forEach((pt, i) => i ? g.lineTo(X(pt[0]!), Y(pt[1]!)) : g.moveTo(X(pt[0]!), Y(pt[1]!))); g.stroke() }

  if (kind === 'chat') {
    brin([[5, 3], [11, 8], [8, 15]], '#3f3640', 2.2)                       // la queue, dressée
    ovale(0, 5, 7.5, 5, '#4a4048', 'grain')                                // corps bas et allongé
    poly([[-6, 10], [-5, 17], [-1.5, 11]], '#4a4048', 'grain')             // oreilles pointues
    poly([[1.5, 10], [5, 17], [6, 10]], '#4a4048', 'grain')
    ovale(0, 11, 5, 4.2, '#5a4e58', 'grain')                               // tête ronde
    oeil(-2.2, 11.5, 1.3); oeil(2.2, 11.5, 1.3)
    g.fillStyle = '#e8a0b0'; g.beginPath(); g.arc(X(0), Y(10), .9 * u, 0, 7); g.fill()
  } else if (kind === 'canari') {
    poly([[3, 9], [12, 13], [4, 4]], '#e0a820', 'grain')                   // queue en éventail
    ovale(0, 8, 6, 6.5, '#f0c93a', 'pois')                                 // corps rond
    poly([[-2, 9], [-9, 9], [-3, 5]], '#ffe07a', 'grain')                  // aile
    g.fillStyle = '#e08010'; g.beginPath()
    g.moveTo(X(-5.5), Y(11)); g.lineTo(X(-9), Y(10)); g.lineTo(X(-5.5), Y(9)); g.fill()
    oeil(-2.4, 11.5, 1.2)
  } else if (kind === 'perroquet') {
    poly([[2, 11], [14, 15], [3, 4]], '#c83a3a', 'rayures')                // longue queue rouge
    ovale(0, 9, 5.5, 8, '#3aa03a', 'rayures')                              // corps dressé
    poly([[-1, 12], [-8, 8], [-1, 4]], '#f0c020', 'grain')                 // aile jaune
    g.fillStyle = '#f0a020'; g.beginPath()
    g.moveTo(X(-4), Y(15)); g.lineTo(X(-9), Y(13.5)); g.lineTo(X(-4), Y(11.5)); g.fill()
    oeil(-1.4, 15, 1.2)
  } else if (kind === 'araignee') {
    for (const s of [-1, 1]) for (let k = 0; k < 3; k++)
      brin([[s * 2, 6], [s * (8 + k * 3), 10 - k], [s * (10 + k * 3.5), 0]], '#2e2438', 1.5)
    ovale(0, 5.5, 6, 4.6, '#2e2438', 'grain')                              // abdomen
    ovale(0, 9.5, 3.4, 3, '#3d3248', 'grain')                              // céphalothorax
    g.fillStyle = '#e04a6a'; g.beginPath(); g.arc(X(0), Y(5), 1.8 * u, 0, 7); g.fill()
    oeil(-1.5, 10.4, 1); oeil(1.5, 10.4, 1)
  } else if (kind === 'serpent') {
    piece('#4a9a4a', 'chevrons', () => {                                   // le S, en une seule découpe
      g.moveTo(X(-11), Y(1)); g.quadraticCurveTo(X(-6), Y(12), X(0), Y(6))
      g.quadraticCurveTo(X(6), Y(0), X(10), Y(9))
      g.lineTo(X(6), Y(11)); g.quadraticCurveTo(X(4), Y(4), X(-1), Y(10))
      g.quadraticCurveTo(X(-6), Y(16), X(-11), Y(4)); g.closePath() })
    ovale(10, 10, 3.4, 2.8, '#6fc46f', 'grain')                            // la tête
    oeil(11, 11, 1); brin([[13, 10], [16, 10.6]], '#e04a4a', 1)
  } else if (kind === 'loup') {
    brin([[8, 5], [16, 9], [15, 15]], '#4e5762', 2.8)                      // queue touffue
    ovale(0, 7, 9.5, 5.5, '#5a6470', 'grain')                              // corps long, bas
    brin([[-5, 2], [-5, 6]], '#4e5762', 2.2); brin([[4, 2], [4, 6]], '#4e5762', 2.2)  // pattes
    poly([[-8, 13], [-9, 20], [-3, 14]], '#5a6470', 'grain')               // oreilles écartées
    poly([[-1, 14], [4, 20], [4, 13]], '#5a6470', 'grain')
    poly([[-9, 14], [-2, 15], [-3, 9], [-14, 10.5]], '#6b7683', 'grain')   // museau allongé
    oeil(-5.5, 13, 1.1); g.fillStyle = '#1e1a24'; g.beginPath(); g.arc(X(-13), Y(10.8), 1.2 * u, 0, 7); g.fill()
  } else if (kind === 'chouette') {
    ovale(0, 8, 7.5, 8.5, '#8a6a48', 'chevrons')                           // corps en obus
    poly([[-7, 15], [-5.5, 20.5], [-2.5, 15.5]], '#8a6a48', 'grain')       // aigrettes
    poly([[2.5, 15.5], [5.5, 20.5], [7, 15]], '#8a6a48', 'grain')
    g.fillStyle = '#f0e8d0'; g.beginPath()                                  // le disque facial
    g.arc(X(-3), Y(12), 3.6 * u, 0, 7); g.arc(X(3), Y(12), 3.6 * u, 0, 7); g.fill()
    oeil(-3, 12, 1.9); oeil(3, 12, 1.9)
    g.fillStyle = '#e0a020'; g.beginPath()
    g.moveTo(X(-1.6), Y(10)); g.lineTo(X(0), Y(7.4)); g.lineTo(X(1.6), Y(10)); g.fill()
    brin([[-3, 1], [-3, 3]], '#e0a020', 1.6); brin([[3, 1], [3, 3]], '#e0a020', 1.6)
  } else if (kind === 'renard') {
    piece('#e8a050', 'grain', () => {                                       // la grande queue, d'abord
      g.moveTo(X(7), Y(4)); g.quadraticCurveTo(X(19), Y(9), X(13), Y(19))
      g.quadraticCurveTo(X(14), Y(9), X(7), Y(7)); g.closePath() })
    ovale(0, 6.5, 9, 5, '#d4762a', 'grain')
    brin([[-5, 1.5], [-5, 5]], '#8a4a1a', 2); brin([[4, 1.5], [4, 5]], '#8a4a1a', 2)
    poly([[-8, 12], [-8, 20], [-2.5, 13]], '#d4762a', 'grain')              // grandes oreilles
    poly([[-1, 13], [3.5, 20], [4, 12]], '#d4762a', 'grain')
    poly([[-9, 13], [-2, 14], [-3, 8.5], [-14, 10]], '#e08a3a', 'grain')    // museau fin
    g.fillStyle = '#f4e8d8'; g.beginPath()                                   // la bavette blanche
    g.moveTo(X(-11), Y(10)); g.lineTo(X(-14), Y(10)); g.lineTo(X(-11.5), Y(8.5)); g.fill()
    oeil(-5.5, 12.5, 1.1); g.fillStyle = '#1e1a24'; g.beginPath(); g.arc(X(-13.4), Y(10.2), 1.2 * u, 0, 7); g.fill()
  } else if (kind === 'dragonnet') {
    piece('#b06ae0', 'marbre', () => {                                      // l'aile déployée
      g.moveTo(X(-1), Y(11)); g.lineTo(X(-13), Y(23)); g.lineTo(X(-9), Y(14))
      g.lineTo(X(-12), Y(15)); g.closePath() })
    brin([[6, 5], [15, 7], [13, 15]], '#8b3ac8', 2.4)                       // la queue
    ovale(0, 8, 7, 6, '#8b3ac8', 'marbre')
    for (let k = 0; k < 3; k++)
      poly([[-1 + k * 3.4, 13.5], [.8 + k * 3.4, 17.5], [2.6 + k * 3.4, 13.5]], '#d8a0f0', 'grain')  // crête
    g.fillStyle = '#ffcf5a'; g.beginPath()                                   // le souffle
    g.moveTo(X(6), Y(9)); g.lineTo(X(12), Y(10)); g.lineTo(X(6), Y(7)); g.fill()
    oeil(3, 10, 1.2)
  } else if (kind === 'taupe') {
    /* TAUPE — rare (vague du 30/07). La bête de la mine par excellence : elle
       creuse, comme le joueur. Silhouette VOLONTAIREMENT très basse et fuselée,
       museau pointu en avant — c'est ce qui la sépare du renard et du chat,
       hauts sur pattes. Les yeux sont deux traits fermés : une taupe ne voit
       pas, et le gag se lit encore à 30 px. */
    brin([[9, 3], [14, 5]], '#8a6a58', 1.8)                                  // la petite queue
    ovale(0, 5.5, 10, 5.5, '#4a3a3e', 'grain')                               // corps bas et fuselé
    poly([[-8, 9], [-17, 6], [-8, 2]], '#5a4850', 'grain')                   // museau en coin
    g.fillStyle = '#e8a0b0'; g.beginPath()                                   // le nez rose
    g.arc(X(-16), Y(5.5), 1.8 * u, 0, 7); g.fill()
    poly([[-6, 1], [-11, 1], [-8.5, 5]], '#f0b8c0', 'grain')                 // pelle avant gauche
    poly([[1, 1], [-3.5, 1], [-1, 5]], '#f0b8c0', 'grain')                   // pelle avant droite
    g.strokeStyle = '#1e1a24'; g.lineWidth = 1.1 * u; g.lineCap = 'round'    // les yeux clos
    for (const dx of [-10, -6.5]) {
      g.beginPath(); g.moveTo(X(dx - 1), Y(7.6)); g.lineTo(X(dx + 1), Y(7.6)); g.stroke()
    }
  } else if (kind === 'chauve-souris') {
    /* CHAUVE-SOURIS — épique. Deux ailes festonnées largement déployées : la
       seule silhouette LARGE du bestiaire, donc reconnaissable même minuscule.
       Les festons sont des arcs RENTRANTS ; sans eux l'aile n'est qu'un triangle
       et l'animal devient un oiseau. */
    for (const s of [-1, 1]) {
      piece(s < 0 ? '#4a3560' : '#3f2c52', 'marbre', () => {
        g.moveTo(X(s * 2), Y(13))
        g.lineTo(X(s * 20), Y(20)); g.lineTo(X(s * 15), Y(13))
        g.lineTo(X(s * 17), Y(11)); g.lineTo(X(s * 11), Y(9))
        g.lineTo(X(s * 12), Y(7)); g.lineTo(X(s * 4), Y(7))
      })
    }
    ovale(0, 10, 5, 6.5, '#2e2438', 'grain')                                 // petit corps dressé
    poly([[-4, 15], [-6, 23], [-.5, 16]], '#2e2438', 'grain')                // grandes oreilles
    poly([[.5, 16], [6, 23], [4, 15]], '#2e2438', 'grain')
    ovale(0, 14, 4.2, 3.8, '#41344f', 'grain')                               // la tête
    oeil(-1.8, 14.5, 1.1); oeil(1.8, 14.5, 1.1)
    g.fillStyle = '#f4e8d8'; g.beginPath()                                   // deux crocs
    g.moveTo(X(-1.6), Y(12)); g.lineTo(X(-.8), Y(10.4)); g.lineTo(X(-.2), Y(12)); g.fill()
    g.beginPath(); g.moveTo(X(.4), Y(12)); g.lineTo(X(1.2), Y(10.4)); g.lineTo(X(1.8), Y(12)); g.fill()
  } else if (kind === 'crapaud') {
    /* CRAPAUD — épique. Large et écrasé, deux bosses d'yeux SUR le dessus du
       crâne : c'est ce profil qui fait crapaud, pas la couleur. Aucune autre
       silhouette basse ET large dans le bestiaire. */
    poly([[7, 1], [14, 1], [10, 7]], '#4e7a34', 'pois')                      // patte arrière repliée
    ovale(0, 5.5, 11, 6.5, '#5c8c3c', 'pois')                                // le corps large
    poly([[-7, 1], [-14, 1], [-10, 6]], '#4e7a34', 'pois')                   // patte avant palmée
    g.strokeStyle = '#2e4a20'; g.lineWidth = 1.2 * u; g.lineCap = 'round'    // la bouche fendue
    g.beginPath(); g.moveTo(X(-10), Y(4.5)); g.quadraticCurveTo(X(-4), Y(2.6), X(2), Y(4.2)); g.stroke()
    for (const dx of [-5, 1.5]) {                                            // les deux bosses d'yeux
      ovale(dx, 12, 3.6, 3.4, '#6a9c46', 'grain')
      g.fillStyle = '#f0d43a'; g.beginPath(); g.arc(X(dx), Y(12.2), 2.2 * u, 0, 7); g.fill()
      oeil(dx, 12.2, 1.1)
    }
    g.fillStyle = rgba('#3a5c26', .55)                                       // trois verrues
    for (const [dx, dy] of [[4, 8], [7.5, 5], [2, 3.5]] as const) {
      g.beginPath(); g.arc(X(dx), Y(dy), 1.5 * u, 0, 7); g.fill()
    }
  } else if (kind === 'follet') {                                            // pas de papier : une lueur
    const gl = g.createRadialGradient(X(0), Y(9), 1, X(0), Y(9), 15 * u)
    gl.addColorStop(0, 'rgba(255,244,190,.95)'); gl.addColorStop(.35, 'rgba(255,210,63,.55)')
    gl.addColorStop(1, 'rgba(255,180,40,0)')
    g.fillStyle = gl; g.fillRect(X(-17), Y(26), 34 * u, 34 * u)
    g.fillStyle = '#fffbe0'; g.beginPath(); g.arc(X(0), Y(9), 3.4 * u, 0, 7); g.fill()
    g.fillStyle = 'rgba(255,235,150,.7)'                                     // les étincelles qui tombent
    for (let k = 0; k < 4; k++) g.beginPath(),
      g.arc(X(-6 + k * 4), Y(3 + (k % 2) * 3), (1.4 - k * .2) * u, 0, 7), g.fill()
  }
}


/* ══════════════════════════ LES TROIS PROPOSITIONS (31/07) ══════════════════════════
 * Demandé par Claude-Alain : « refais le design de tous les pets, chacun en 3 versions,
 * plus détaillés et fun, je veux du premium ».
 *
 * ⚠️ LA CONTRAINTE QUI COMMANDE TOUT : EN JEU, UN COMPAGNON FAIT 22 À 35 PIXELS.
 * `PET_SCALE` (client/pet.ts) va de 0,46 à 0,72 pour une case de 48. Le canari fait 22 px de
 * haut, le loup 35. À cette taille, « plus détaillé » à l'intérieur du corps ne se voit pas :
 * ce qui se voit, c'est la SILHOUETTE, le CONTRASTE et UN accessoire franc. Les trois directions
 * ci-dessous cherchent donc le premium par là, pas par la densité de traits.
 *
 * Les trois directions, appliquées aux treize compagnons :
 *   1 · FRANC      — la silhouette poussée à fond. Deux ou trois aplats, un contraste net, un
 *                    détail signature. C'est la direction actuelle, mais assumée jusqu'au bout.
 *   2 · PELUCHE    — grosse tête, grands yeux brillants, tout arrondi. Le plus lisible de tous
 *                    à 22 px, et celui qui parle le plus aux enfants.
 *   3 · BAROUDEUR  — l'animal ÉQUIPÉ pour la mine : casque à lampe, écharpe, lunettes, besace.
 *                    Le plus « premium » parce que le plus spécifique à ce jeu-ci.
 */

/** La boîte à outils partagée par les trois directions. */
interface Kit {
  g: Ctx
  u: number
  X: (v: number) => number
  Y: (v: number) => number
  piece: (col: string, motif: string, trace: () => void) => void
  ovale: (dx: number, dy: number, rx: number, ry: number, col: string, motif: string, rot?: number) => void
  poly: (pts: number[][], col: string, motif: string) => void
  brin: (pts: number[][], col: string, w: number) => void
  oeil: (dx: number, dy: number, r?: number) => void
  /** Grand œil brillant : iris sombre + éclat blanc décalé. C'est LUI qui fait « peluche ». */
  oeilVif: (dx: number, dy: number, r: number, iris?: string) => void
  /** Casque de mineur à lampe allumée — le lien direct avec l'univers du jeu. */
  casque: (dx: number, dy: number, e: number) => void
  /** Écharpe flottante : deux découpes, une au cou, une qui claque derrière. */
  echarpe: (dx: number, dy: number, e: number, col: string, sens?: number) => void
  /** Lunettes de protection remontées sur le front. */
  lunettes: (dx: number, dy: number, e: number) => void
  /** Petite besace de cuir en bandoulière. */
  besace: (dx: number, dy: number, e: number) => void
}

function faireKit(g: Ctx, cx: number, base: number, T: number): Kit {
  const u = T / 26
  const X = (v: number): number => cx + v * u, Y = (v: number): number => base - v * u
  // ⚠️ Plus haute que large : le dragonnet aux ailes déployées monte à 31 unités, là où un chat
  // en fait 18. Bornée à 30, la découpe lui coupait le haut des ailes.
  const boite = { x: cx - 26 * u, y: base - 36 * u, w: 52 * u, h: 42 * u }
  const piece = (col: string, motif: string, trace: () => void): void =>
    decoupe(g, () => { g.beginPath(); trace() }, col, motif, boite, 2.5 * u, 3 * u)
  const ovale = (dx: number, dy: number, rx: number, ry: number, col: string, motif: string, rot = 0): void =>
    piece(col, motif, () => g.ellipse(X(dx), Y(dy), rx * u, ry * u, rot, 0, 7))
  const poly = (pts: number[][], col: string, motif: string): void => piece(col, motif, () => {
    pts.forEach((pt, i) => i ? g.lineTo(X(pt[0]!), Y(pt[1]!)) : g.moveTo(X(pt[0]!), Y(pt[1]!))); g.closePath() })
  const brin = (pts: number[][], col: string, w: number): void => {
    g.strokeStyle = col; g.lineWidth = w * u; g.lineCap = 'round'; g.lineJoin = 'round'
    g.beginPath(); pts.forEach((pt, i) => i ? g.lineTo(X(pt[0]!), Y(pt[1]!)) : g.moveTo(X(pt[0]!), Y(pt[1]!))); g.stroke() }
  const oeil = (dx: number, dy: number, r = 1.5): void => {
    g.fillStyle = '#1e1a24'; g.beginPath(); g.arc(X(dx), Y(dy), r * u, 0, 7); g.fill() }
  const oeilVif = (dx: number, dy: number, r: number, iris = '#1e1a24'): void => {
    g.fillStyle = '#fdfaf2'; g.beginPath(); g.arc(X(dx), Y(dy), r * u, 0, 7); g.fill()
    g.fillStyle = iris; g.beginPath(); g.arc(X(dx + r * .12), Y(dy - r * .1), r * .62 * u, 0, 7); g.fill()
    g.fillStyle = 'rgba(255,255,255,.94)'                                     // l'éclat : c'est LUI qui donne le regard
    g.beginPath(); g.arc(X(dx - r * .32), Y(dy + r * .34), r * .27 * u, 0, 7); g.fill() }
  const casque = (dx: number, dy: number, e: number): void => {
    piece('#e8a020', 'grain', () => {                                        // la coque
      g.moveTo(X(dx - 5 * e), Y(dy)); g.quadraticCurveTo(X(dx), Y(dy + 7 * e), X(dx + 5 * e), Y(dy))
      g.lineTo(X(dx + 6.2 * e), Y(dy - .8 * e)); g.lineTo(X(dx - 6.2 * e), Y(dy - .8 * e)); g.closePath() })
    g.fillStyle = '#fff3b0'; g.beginPath()                                    // la lampe
    g.arc(X(dx), Y(dy + 4.2 * e), 1.5 * e * u, 0, 7); g.fill()
    g.fillStyle = 'rgba(255,236,140,.32)'; g.beginPath()                      // son halo
    g.arc(X(dx), Y(dy + 4.2 * e), 3.4 * e * u, 0, 7); g.fill() }
  const echarpe = (dx: number, dy: number, e: number, col: string, sens = 1): void => {
    piece(col, 'rayures', () => {                                             // le pan qui claque
      g.moveTo(X(dx), Y(dy)); g.lineTo(X(dx - sens * 9 * e), Y(dy + 2 * e))
      g.lineTo(X(dx - sens * 10 * e), Y(dy - 1.6 * e)); g.lineTo(X(dx), Y(dy - 2.4 * e)); g.closePath() })
    piece(col, 'rayures', () => g.ellipse(X(dx), Y(dy - .4 * e), 4 * e * u, 1.9 * e * u, 0, 0, 7)) }
  const lunettes = (dx: number, dy: number, e: number): void => {
    g.strokeStyle = '#6b5a3a'; g.lineWidth = 1.3 * e * u
    g.beginPath(); g.moveTo(X(dx - 5 * e), Y(dy)); g.lineTo(X(dx + 5 * e), Y(dy)); g.stroke()
    for (const s of [-1, 1]) {
      g.fillStyle = '#7fd8e8'; g.beginPath(); g.arc(X(dx + s * 2.6 * e), Y(dy), 2 * e * u, 0, 7); g.fill()
      g.strokeStyle = '#c8a44a'; g.lineWidth = .8 * e * u
      g.beginPath(); g.arc(X(dx + s * 2.6 * e), Y(dy), 2 * e * u, 0, 7); g.stroke() } }
  const besace = (dx: number, dy: number, e: number): void => {
    g.strokeStyle = '#7a5a34'; g.lineWidth = 1.1 * e * u
    g.beginPath(); g.moveTo(X(dx - 3 * e), Y(dy + 5 * e)); g.lineTo(X(dx + 3 * e), Y(dy)); g.stroke()
    poly([[dx + 1.5 * e, dy + 1.5 * e], [dx + 5.5 * e, dy + 1.5 * e], [dx + 5.5 * e, dy - 2.5 * e], [dx + 1.5 * e, dy - 2.5 * e]], '#9a6f3e', 'quadrille')
    g.fillStyle = '#f0c93a'; g.beginPath(); g.arc(X(dx + 3.5 * e), Y(dy + 2 * e), .9 * e * u, 0, 7); g.fill() }
  return { g, u, X, Y, piece, ovale, poly, brin, oeil, oeilVif, casque, echarpe, lunettes, besace }
}

/* ──────────────────────────── 1 · FRANC ────────────────────────────
 * La silhouette poussée à fond : deux ou trois aplats, un contraste net, UN détail signature.
 * C'est la direction historique du jeu, mais assumée jusqu'au bout — dos cambré, queue haute,
 * museau plus long. Rien à l'intérieur du corps : à 22 px, l'intérieur n'existe pas. */
function petFranc(k: Kit, kind: string): void {
  const { g, u, X, Y, ovale, poly, brin, oeil, piece } = k
  const tache = (dx: number, dy: number, rx: number, ry: number, col = '#f4ecdc'): void => {
    g.fillStyle = col; g.beginPath(); g.ellipse(X(dx), Y(dy), rx * u, ry * u, 0, 0, 7); g.fill() }
  if (kind === 'chat') {
    brin([[6, 4], [13, 10], [10, 17], [6, 16]], '#332c36', 2.6)              // queue haute, crochetée
    piece('#3d3540', 'grain', () => {                                        // dos CAMBRÉ : la pose du chat
      g.moveTo(X(-8), Y(3)); g.quadraticCurveTo(X(-2), Y(14), X(7), Y(4))
      g.lineTo(X(6), Y(1)); g.lineTo(X(-7), Y(1)); g.closePath() })
    brin([[-5, 1], [-5, 4]], '#2a242e', 2); brin([[4, 1], [4, 4]], '#2a242e', 2)
    tache(-3, 3.5, 3.2, 2.4)                                                 // le plastron clair
    poly([[-7, 11], [-6, 18], [-2, 12]], '#3d3540', 'grain')
    poly([[1, 12], [5, 18], [6, 11]], '#3d3540', 'grain')
    ovale(-.5, 11.5, 5.2, 4.4, '#4d4450', 'grain')
    g.fillStyle = '#8fd06a'; g.beginPath(); g.arc(X(-2.4), Y(12), 1.5 * u, 0, 7); g.fill()
    g.beginPath(); g.arc(X(2.2), Y(12), 1.5 * u, 0, 7); g.fill()
    oeil(-2.4, 12, .7); oeil(2.2, 12, .7)
    g.fillStyle = '#e8a0b0'; g.beginPath(); g.arc(X(-.1), Y(10.2), 1 * u, 0, 7); g.fill()
  } else if (kind === 'canari') {
    poly([[3, 8], [15, 14], [14, 6], [4, 3]], '#d99a12', 'rayures')          // queue en éventail LARGE
    ovale(0, 8, 7, 7.5, '#ffd23f', 'pois')                                   // corps bien rond
    poly([[-1, 10], [-10, 8], [-2, 4]], '#ffe89a', 'grain')                  // aile claire
    poly([[-1, 15], [1, 19], [2.5, 15]], '#ffd23f', 'grain')                 // petite huppe
    g.fillStyle = '#ef7a10'; g.beginPath()
    g.moveTo(X(-6), Y(11.5)); g.lineTo(X(-10.5), Y(10)); g.lineTo(X(-6), Y(8.5)); g.fill()
    oeil(-2.6, 12, 1.3)
  } else if (kind === 'loup') {
    piece('#48525e', 'grain', () => {                                        // queue TOUFFUE, en découpe
      g.moveTo(X(8), Y(6)); g.quadraticCurveTo(X(20), Y(11), X(15), Y(19))
      g.quadraticCurveTo(X(15), Y(9), X(8), Y(9)); g.closePath() })
    ovale(0, 7.5, 10, 5.8, '#59636f', 'grain')
    brin([[-6, 1], [-6, 5]], '#3f4854', 2.4); brin([[-2, 1], [-2, 5]], '#3f4854', 2.4)
    brin([[3, 1], [3, 5]], '#3f4854', 2.4); brin([[6.5, 1], [6.5, 5]], '#3f4854', 2.4)
    tache(-2, 4, 5, 2.6, '#cfd6de')                                          // le ventre clair
    poly([[-9, 13], [-10, 21], [-3, 14]], '#59636f', 'grain')
    poly([[-1, 14], [4, 21], [4.5, 13]], '#59636f', 'grain')
    poly([[-10, 14], [-2, 15], [-3, 9], [-16, 10.5]], '#6d7885', 'grain')    // museau LONG
    tache(-12, 10.5, 3, 1.6, '#cfd6de')
    g.fillStyle = '#ffc93a'; g.beginPath(); g.arc(X(-6), Y(13.4), 1.5 * u, 0, 7); g.fill()
    oeil(-6, 13.4, .8)
    g.fillStyle = '#1e1a24'; g.beginPath(); g.arc(X(-15), Y(10.8), 1.4 * u, 0, 7); g.fill()
  } else if (kind === 'perroquet') {
    poly([[2, 11], [16, 17], [15, 8], [3, 4]], '#d43c3c', 'rayures')         // queue immense
    ovale(0, 9, 6, 8.5, '#2f9c46', 'rayures')
    poly([[-1, 13], [-9, 9], [-8, 3], [-1, 4]], '#f0c020', 'grain')          // aile jaune franche
    poly([[-1, 16], [1, 21], [3, 16]], '#d43c3c', 'grain')                   // huppe
    g.fillStyle = '#f7a81e'; piece('#f7a81e', 'grain', () => {               // GROS bec crochu
      g.moveTo(X(-4), Y(16.5)); g.quadraticCurveTo(X(-11), Y(15), X(-7), Y(11))
      g.quadraticCurveTo(X(-6.5), Y(14), X(-4), Y(13.5)); g.closePath() })
    g.fillStyle = '#fdfaf2'; g.beginPath(); g.arc(X(-1.8), Y(16), 2 * u, 0, 7); g.fill()
    oeil(-2, 16, 1.1)
  } else if (kind === 'araignee') {
    for (const s of [-1, 1]) for (let j = 0; j < 4; j++)                     // 8 pattes ARQUÉES
      brin([[s * 2, 6], [s * (7 + j * 2.4), 12 - j * .8], [s * (11 + j * 3.2), 0]], '#241c2e', 1.7)
    ovale(0, 6, 7, 5.4, '#241c2e', 'grain')
    piece('#e0304a', 'grain', () => {                                        // le sablier rouge
      g.moveTo(X(-2.4), Y(9)); g.lineTo(X(2.4), Y(9)); g.lineTo(X(.9), Y(6))
      g.lineTo(X(2.4), Y(3)); g.lineTo(X(-2.4), Y(3)); g.lineTo(X(-.9), Y(6)); g.closePath() })
    ovale(0, 10.5, 3.8, 3.2, '#372c46', 'grain')
    for (const d of [-2.2, -.7, .7, 2.2]) oeil(d, 11.2, .8)                  // quatre petits yeux
  } else if (kind === 'serpent') {
    /* ⚠️ REFAIT (31/07) : la première version était une VAGUE PLATE horizontale, avec une tête
       à peine plus grosse que le corps. À 32 px elle se lisait comme une chenille verte.
       Ce qui fait « serpent » : un corps ENROULÉ posé au sol, un cou dressé en S, et une tête
       en COIN nettement séparée. Trois ruptures de silhouette au lieu d'une ondulation. */
    ovale(2, 4.5, 11, 5.5, '#2f7a36', 'chevrons')                            // l'anneau extérieur
    ovale(2.5, 4.8, 6.2, 3, '#43a24a', 'grain')                              // l'anneau intérieur : l'enroulement se VOIT
    for (const [dx, dy] of [[-6, 6.5], [8.5, 6], [1, 1.6]] as const)         // losanges dorsaux
      poly([[dx, dy + 2], [dx + 2, dy], [dx, dy - 2], [dx - 2, dy]], '#d8e86a', 'grain')
    piece('#3f8f42', 'chevrons', () => {                                     // le COU dressé, qui s'affine
      g.moveTo(X(1), Y(7)); g.quadraticCurveTo(X(-7), Y(10), X(-5.5), Y(16.5))
      g.lineTo(X(-1.5), Y(16)); g.quadraticCurveTo(X(-3), Y(11), X(4.5), Y(8.6)); g.closePath() })
    piece('#5fbe62', 'grain', () => {                                        // la tête en COIN, franche
      g.moveTo(X(-1), Y(18.5)); g.lineTo(X(-12.5), Y(16.8)); g.lineTo(X(-1), Y(14.5)); g.closePath() })
    g.fillStyle = '#ffd23f'; g.beginPath(); g.arc(X(-4), Y(17.4), 1.7 * u, 0, 7); g.fill()
    g.fillStyle = '#1e1a24'; g.beginPath()                                    // pupille FENDUE verticale
    g.ellipse(X(-4), Y(17.4), .55 * u, 1.5 * u, 0, 0, 7); g.fill()
    brin([[-12, 16.8], [-16.5, 17.8]], '#e04a4a', 1.1)                        // langue fourchue
    brin([[-14.5, 17.2], [-16.5, 15.8]], '#e04a4a', 1)
  } else if (kind === 'dragonnet') {
    /* ⚠️ REFAIT UNE SECONDE FOIS (31/07) — « toujours super moche ». Il avait raison, et j'ai
       cette fois cherché à quoi ressemble un dragon au lieu de le supposer.
       Trois erreurs de fond dans mes deux premières versions :
        1. AUCUN COU. Ce qui fait lire « dragon » plutôt que « monstre », c'est un long cou en S
           portant un crâne à cornes. Ma tête était collée au corps : c'était un chat cornu.
        2. DES AILES REPLIÉES ALORS QU'IL VOLE. `FLYING_PETS` le fait planer à côté du héros —
           des ailes fermées contredisaient son propre comportement. Elles sont déployées.
        3. UNE MEMBRANE EN TRIANGLE. Une aile de dragon suit la main d'une chauve-souris : bord
           supérieur en « M » brisé par les doigts, et FESTONS creux entre eux. Le triangle plein
           se lisait comme un aileron.
       Et il est deux fois plus grand qu'avant (PET_SCALE 0,58 -> 1,16), à sa demande : un cou, des
       ailes ouvertes et une queue enroulée ont besoin de place pour exister. */
    const aile = (ax: number, ay: number, e: number, col: string): void => piece(col, 'marbre', () => {
      g.moveTo(X(ax), Y(ay))                                                   // l'épaule
      g.lineTo(X(ax + 4 * e), Y(ay + 14 * e))                                  // doigt 1
      g.quadraticCurveTo(X(ax + 6.5 * e), Y(ay + 8 * e), X(ax + 10.5 * e), Y(ay + 15 * e))  // feston creux
      g.quadraticCurveTo(X(ax + 13 * e), Y(ay + 8.5 * e), X(ax + 16.5 * e), Y(ay + 11 * e)) // doigt 3
      g.quadraticCurveTo(X(ax + 12 * e), Y(ay + 5 * e), X(ax + 4 * e), Y(ay + 2.5 * e))     // bord de fuite
      g.closePath() })
    // ⚠️ GRANDES. Au premier essai elles faisaient 1,05 et 0,8 d'échelle : noyées derrière le
    // corps, elles se lisaient comme des nageoires. L'aile est, avec le cou, LE signe du dragon.
    aile(2, 10.5, 1.45, '#5f2196')                                             // aile ARRIÈRE, plus sombre
    piece('#7d31bd', 'grain', () => {                                          // la queue, enroulée sous le corps
      g.moveTo(X(6), Y(5)); g.quadraticCurveTo(X(18), Y(2), X(16), Y(11))
      g.quadraticCurveTo(X(15), Y(6), X(6), Y(8)); g.closePath() })
    poly([[15.5, 11.5], [21, 13.5], [15.5, 8]], '#e8b8ff', 'grain')            // le fer de lance au bout
    ovale(2, 8, 6.2, 5.2, '#8b3ac8', 'marbre')                                 // le corps, compact
    g.fillStyle = '#e6c8f6'; g.beginPath()                                      // le ventre écaillé, plus clair
    g.ellipse(X(1), Y(6), 4.2 * u, 2.6 * u, 0, 0, 7); g.fill()
    for (const dx of [-1, 3.5]) {                                               // deux pattes griffues, repliées
      poly([[dx, 4.5], [dx - 1.8, .6], [dx + 1.6, 1.2]], '#6d2aa8', 'grain')
      brin([[dx - 1.8, .8], [dx - 3.2, -.2]], '#e8b8ff', 1) }
    piece('#8b3ac8', 'marbre', () => {                                          // LE COU, long et en S
      g.moveTo(X(-1), Y(11)); g.quadraticCurveTo(X(-9), Y(12), X(-9.5), Y(19))
      g.lineTo(X(-5.5), Y(19.5)); g.quadraticCurveTo(X(-5), Y(14), X(2.5), Y(12.5)); g.closePath() })
    for (let j = 0; j < 5; j++)                                                 // crête, du crâne à la queue
      poly([[-6 + j * 3, 13 + (j < 2 ? 5 : 0)], [-4.8 + j * 3, 16.6 + (j < 2 ? 5 : 0)], [-3.6 + j * 3, 13 + (j < 2 ? 5 : 0)]], '#e8b8ff', 'grain')
    piece('#9a45d8', 'grain', () => {                                           // LE CRÂNE : un coin, pas un rond
      g.moveTo(X(-4.5), Y(22.5)); g.lineTo(X(-13), Y(21.5)); g.lineTo(X(-16.5), Y(18.5))
      g.lineTo(X(-12), Y(16.5)); g.lineTo(X(-4.5), Y(17.5)); g.closePath() })
    poly([[-6, 22], [-2, 27.5], [-3, 21]], '#e0d0f0', 'grain')                  // deux cornes, vers l'arrière
    poly([[-9, 22.5], [-5.5, 27], [-6.5, 21.5]], '#e0d0f0', 'grain')
    g.strokeStyle = '#5f2196'; g.lineWidth = 1 * u; g.lineCap = 'round'         // la ligne de mâchoire
    g.beginPath(); g.moveTo(X(-15.5), Y(18.2)); g.lineTo(X(-6), Y(18.6)); g.stroke()
    g.fillStyle = '#ffd23f'; g.beginPath(); g.arc(X(-9.5), Y(20.3), 1.7 * u, 0, 7); g.fill()
    g.fillStyle = '#1e1a24'; g.beginPath()
    g.ellipse(X(-9.5), Y(20.3), .55 * u, 1.5 * u, 0, 0, 7); g.fill()            // pupille de reptile
    for (const [dx, dy, r, col] of [[-19.5, 18.6, 2.6, '#ff9a2e'], [-22.8, 19.5, 1.8, '#ffc93a'], [-25, 18.4, 1.1, '#ffe89a']] as const) {
      g.fillStyle = col; g.beginPath(); g.arc(X(dx), Y(dy), r * u, 0, 7); g.fill() }
    aile(-2.5, 10, 1.1, '#a05cd8')                                              // aile AVANT, plus claire
  } else if (kind === 'chouette') {
    piece('#7d5f40', 'chevrons', () => {                                     // corps en obus LARGE
      g.moveTo(X(-8.5), Y(2)); g.quadraticCurveTo(X(-9.5), Y(17), X(0), Y(18))
      g.quadraticCurveTo(X(9.5), Y(17), X(8.5), Y(2)); g.closePath() })
    poly([[-8, 15], [-6, 22], [-2.5, 16]], '#7d5f40', 'grain')
    poly([[2.5, 16], [6, 22], [8, 15]], '#7d5f40', 'grain')
    g.fillStyle = '#f4ead2'; g.beginPath()                                    // disque facial en 8
    g.arc(X(-3.4), Y(12.5), 4.1 * u, 0, 7); g.arc(X(3.4), Y(12.5), 4.1 * u, 0, 7); g.fill()
    for (const s of [-1, 1]) {
      g.fillStyle = '#f0a81e'; g.beginPath(); g.arc(X(s * 3.4), Y(12.5), 2.5 * u, 0, 7); g.fill()
      oeil(s * 3.4, 12.5, 1.4) }
    g.fillStyle = '#e0821a'; g.beginPath()
    g.moveTo(X(-1.7), Y(10.4)); g.lineTo(X(0), Y(7.2)); g.lineTo(X(1.7), Y(10.4)); g.fill()
    brin([[-3, 1], [-3, 3]], '#e0a020', 1.8); brin([[3, 1], [3, 3]], '#e0a020', 1.8)
  } else if (kind === 'renard') {
    piece('#e8a050', 'grain', () => {                                        // la queue, PLUS GROSSE que le corps
      g.moveTo(X(6), Y(4)); g.quadraticCurveTo(X(22), Y(8), X(15), Y(21))
      g.quadraticCurveTo(X(16), Y(8), X(6), Y(8)); g.closePath() })
    g.fillStyle = '#fdfaf2'; g.beginPath()                                    // le bout blanc
    g.ellipse(X(16.5), Y(19), 3 * u, 2.6 * u, .5, 0, 7); g.fill()
    ovale(0, 7, 9.5, 5.2, '#d4762a', 'grain')
    tache(-2, 4, 5, 2.4)
    brin([[-5, 1], [-5, 5]], '#33261e', 2.2); brin([[4, 1], [4, 5]], '#33261e', 2.2)  // chaussettes noires
    poly([[-9, 12], [-9, 21], [-2.5, 13]], '#d4762a', 'grain')
    poly([[-1, 13], [4, 21], [4.5, 12]], '#d4762a', 'grain')
    poly([[-10, 13], [-2, 14], [-3, 8.5], [-16, 10]], '#e89a4a', 'grain')
    tache(-13, 9.6, 3, 1.5)
    g.fillStyle = '#ffc93a'; g.beginPath(); g.arc(X(-6), Y(12.6), 1.4 * u, 0, 7); g.fill()
    oeil(-6, 12.6, .75)
    g.fillStyle = '#1e1a24'; g.beginPath(); g.arc(X(-15.2), Y(10.2), 1.3 * u, 0, 7); g.fill()
  } else if (kind === 'follet') {
    const gl = g.createRadialGradient(X(0), Y(10), 1, X(0), Y(10), 17 * u)    // le halo, plus large
    gl.addColorStop(0, 'rgba(255,248,210,.98)'); gl.addColorStop(.3, 'rgba(255,205,70,.6)')
    gl.addColorStop(1, 'rgba(255,170,30,0)')
    g.fillStyle = gl; g.fillRect(X(-19), Y(29), 38 * u, 38 * u)
    g.fillStyle = '#fffbe0'; g.beginPath()                                    // une FLAMME, pas une bille
    g.moveTo(X(0), Y(18)); g.quadraticCurveTo(X(4.6), Y(11), X(2.6), Y(6))
    g.quadraticCurveTo(X(0), Y(3), X(-2.6), Y(6))
    g.quadraticCurveTo(X(-4.6), Y(11), X(0), Y(18)); g.fill()
    g.fillStyle = '#ffb02e'; g.beginPath()
    g.moveTo(X(0), Y(13)); g.quadraticCurveTo(X(2), Y(9.5), X(1.2), Y(7))
    g.quadraticCurveTo(X(0), Y(5.6), X(-1.2), Y(7))
    g.quadraticCurveTo(X(-2), Y(9.5), X(0), Y(13)); g.fill()
    oeil(-1.5, 9.5, .8); oeil(1.5, 9.5, .8)                                   // deux yeux : ça devient un être
    g.fillStyle = 'rgba(255,235,150,.75)'
    for (let j = 0; j < 5; j++) { g.beginPath(); g.arc(X(-8 + j * 4), Y(2 + (j % 2) * 3.4), (1.5 - j * .18) * u, 0, 7); g.fill() }
  } else if (kind === 'taupe') {
    brin([[10, 3], [15, 6]], '#8a6a58', 2)
    ovale(0, 5.5, 10.5, 5.8, '#443438', 'grain')
    tache(-1, 3, 5, 2.2, '#7a6068')
    poly([[-8, 9.5], [-18, 6], [-8, 2]], '#55424a', 'grain')                  // museau en COIN, très long
    g.fillStyle = '#f0a8b8'; g.beginPath(); g.arc(X(-17), Y(5.6), 2 * u, 0, 7); g.fill()
    poly([[-5, .5], [-11.5, .5], [-8.5, 6]], '#f4c0c8', 'grain')              // GROSSES pelles roses
    poly([[2, .5], [-3.5, .5], [-.5, 6]], '#f4c0c8', 'grain')
    g.strokeStyle = '#1e1a24'; g.lineWidth = 1.2 * u; g.lineCap = 'round'
    for (const dx of [-10.5, -6.8]) { g.beginPath(); g.moveTo(X(dx - 1.1), Y(7.8)); g.lineTo(X(dx + 1.1), Y(7.8)); g.stroke() }
  } else if (kind === 'chauve-souris') {
    for (const s of [-1, 1]) piece(s < 0 ? '#513a68' : '#412e56', 'marbre', () => {
      g.moveTo(X(s * 2), Y(14))
      g.lineTo(X(s * 22), Y(22)); g.lineTo(X(s * 16.5), Y(14.5))
      g.lineTo(X(s * 19), Y(12)); g.lineTo(X(s * 12), Y(9.5))
      g.lineTo(X(s * 13), Y(7)); g.lineTo(X(s * 4.5), Y(7.5)); g.closePath() })
    ovale(0, 10.5, 5.2, 6.8, '#2e2438', 'grain')
    poly([[-4.5, 15.5], [-7, 24], [-.5, 16.5]], '#2e2438', 'grain')
    poly([[.5, 16.5], [7, 24], [4.5, 15.5]], '#2e2438', 'grain')
    ovale(0, 14.5, 4.4, 4, '#453757', 'grain')
    g.fillStyle = '#ff7a4a'; g.beginPath(); g.arc(X(-1.9), Y(15), 1.4 * u, 0, 7); g.fill()
    g.beginPath(); g.arc(X(1.9), Y(15), 1.4 * u, 0, 7); g.fill()
    oeil(-1.9, 15, .7); oeil(1.9, 15, .7)
    g.fillStyle = '#fdfaf2'
    for (const dx of [-1.5, 1.5]) { g.beginPath()
      g.moveTo(X(dx - .8), Y(12.4)); g.lineTo(X(dx), Y(10.6)); g.lineTo(X(dx + .8), Y(12.4)); g.fill() }
  } else if (kind === 'crapaud') {
    /* ⚠️ REFAIT (31/07) : la première version était une MOTTE LISSE. Un crapaud se reconnaît à
       ses PATTES ÉCARTÉES qui cassent la silhouette et à sa bouche fendue d'un bord à l'autre —
       pas à sa couleur. Genoux relevés sur les côtés, doigts palmés en éventail devant, et une
       gorge qui gonfle. Trois ruptures au lieu d'un dôme. */
    for (const sx of [-1, 1]) piece('#43682b', 'pois', () => {               // cuisses repliées, genou HAUT
      g.moveTo(X(sx * 7), Y(2)); g.quadraticCurveTo(X(sx * 15), Y(11), X(sx * 15.5), Y(3))
      g.quadraticCurveTo(X(sx * 14), Y(.5), X(sx * 8), Y(.6)); g.closePath() })
    ovale(0, 5, 11, 5.6, '#5c8c3c', 'pois')                                  // corps large et ÉCRASÉ
    for (const sx of [-7.5, 3.5]) for (let j = 0; j < 3; j++)                // doigts palmés en éventail
      brin([[sx + j * .9, 1.8], [sx - 2.6 + j * 2.4, -.6]], '#7fae52', 1.7)
    g.fillStyle = '#cfe0a0'; g.beginPath()                                    // la gorge qui gonfle
    g.ellipse(X(-1), Y(2.6), 6.4 * u, 2.4 * u, 0, 0, 7); g.fill()
    g.strokeStyle = '#25411a'; g.lineWidth = 1.6 * u; g.lineCap = 'round'     // LA bouche, d'un bord à l'autre
    g.beginPath(); g.moveTo(X(-10.5), Y(5.4)); g.quadraticCurveTo(X(-2), Y(2.4), X(6.5), Y(5.2)); g.stroke()
    for (const dx of [-5, 2.5]) {                                             // les deux dômes d'yeux
      ovale(dx, 11.5, 4.2, 4, '#6a9c46', 'grain')
      g.fillStyle = '#f5d92e'; g.beginPath(); g.arc(X(dx), Y(11.6), 2.6 * u, 0, 7); g.fill()
      g.fillStyle = '#1e1a24'; g.beginPath()
      g.ellipse(X(dx), Y(11.6), 2.2 * u, .8 * u, 0, 0, 7); g.fill()           // pupille HORIZONTALE : c'est un crapaud
    }
    g.fillStyle = rgba('#33521f', .55)
    for (const [dx, dy] of [[6.5, 8], [9, 5], [-9, 7.5]] as const) {
      g.beginPath(); g.arc(X(dx), Y(dy), 1.7 * u, 0, 7); g.fill() }
  }

  /* ── LA VAGUE DU 05/08 (planche /pages/cosmetiques2.html) ─────────────────
     Quatre bêtes proposées, hors catalogue tant que Claude-Alain n'a pas
     tranché. Écrites dans le style Franc : silhouette d'abord, UN détail
     signature, rien à l'intérieur du corps. */
  if (kind === 'herisson') {
    /* LE HÉRISSON : tout est dans le manteau DENTELÉ — un dôme lisse ferait une
       motte (la leçon du crapaud). Sept pointes, pas plus : à 22 px, davantage
       devient une frange floue. */
    piece('#4e3f34', 'grain', () => {
      g.moveTo(X(-10), Y(2.5))
      for (const [px, py] of [[-11.5, 7.5], [-8.2, 7.2], [-8.5, 12.5], [-4.8, 10.4], [-4.2, 15],
        [-1.2, 11.8], [.5, 15.8], [2.8, 11.6], [5, 14.2], [6.4, 10.6], [9.2, 11.2], [9.6, 7.6], [12, 6.5]] as const) {
        g.lineTo(X(px), Y(py))
      }
      g.lineTo(X(10.5), Y(2.5)); g.closePath() })
    g.strokeStyle = '#7a6650'; g.lineWidth = 1 * u; g.lineCap = 'round'      // trois piquants clairs
    for (const [x1, y1, x2, y2] of [[-6.5, 8.5, -8, 11.5], [-.5, 10, -1.5, 14], [5.5, 9, 7, 12]] as const) {
      g.beginPath(); g.moveTo(X(x1), Y(y1)); g.lineTo(X(x2), Y(y2)); g.stroke()
    }
    ovale(-4, 4, 8, 3.6, '#c9a97e', 'grain')                                 // le ventre clair
    brin([[-5, .5], [-5, 2.5]], '#6b5844', 1.8); brin([[2, .5], [2, 2.5]], '#6b5844', 1.8)
    poly([[-9, 6], [-15, 3.2], [-9, 2.2]], '#b8946a', 'grain')               // le museau pointu
    g.fillStyle = '#1e1a24'; g.beginPath(); g.arc(X(-14.6), Y(3.5), 1.2 * u, 0, 7); g.fill()  // la truffe
    oeil(-8.2, 5.6, .9)
  } else if (kind === 'golem') {
    /* LE GOLEM : trois pierres EMPILÉES DE TRAVERS — l'équilibre précaire fait
       le vivant, un empilement droit ferait un cairn. Le regard, deux braises
       ambre, est le seul point chaud de toute la bête. */
    ovale(0, 4, 9, 4.4, '#6b7280', 'grain')                                  // le socle
    ovale(-8.5, 7.5, 2.4, 2, '#5f6874', 'grain'); ovale(7.5, 8, 2.6, 2.2, '#5f6874', 'grain')  // les poings
    ovale(-1, 10, 6.5, 3.8, '#7d8894', 'grain', -.12)                        // le torse, de travers
    ovale(-3.5, 15.5, 4, 3.2, '#8d99a6', 'grain', .1)                        // la tête, décalée
    g.fillStyle = '#5fbf4f'                                                  // la mousse : il est VIEUX
    g.beginPath(); g.ellipse(X(3.2), Y(6.2), 2 * u, 1.1 * u, -.3, 0, 7); g.fill()
    g.beginPath(); g.ellipse(X(-6.2), Y(11.4), 1.5 * u, .9 * u, .4, 0, 7); g.fill()
    brin([[4, 2.6], [6, 5.2], [5, 7]], '#4a525c', 1)                         // la fissure
    g.fillStyle = '#ffc93a'                                                  // les braises du regard
    g.beginPath(); g.arc(X(-5.2), Y(15.8), 1 * u, 0, 7); g.fill()
    g.beginPath(); g.arc(X(-2), Y(16.1), 1 * u, 0, 7); g.fill()
  } else if (kind === 'axolotl') {
    /* L'AXOLOTL : la signature est l'éventail de BRANCHIES derrière la tête,
       trois brins terminés d'une boule — et le sourire. Sans eux c'est un
       triton rose ; avec eux, tout le monde le nomme du premier coup. */
    piece('#f4a0b8', 'grain', () => {                                        // la queue-voile
      g.moveTo(X(5), Y(3)); g.quadraticCurveTo(X(16), Y(9), X(13.5), Y(13))
      g.quadraticCurveTo(X(9), Y(9.5), X(5.5), Y(8)); g.closePath() })
    ovale(0, 4.5, 9.5, 4, '#f7b6c9', 'grain')                                // le corps bas
    g.fillStyle = '#fde4ec'                                                  // le ventre
    g.beginPath(); g.ellipse(X(0), Y(2.8), 6 * u, 1.7 * u, 0, 0, 7); g.fill()
    brin([[-6, .5], [-6, 2.2]], '#e8899f', 1.6); brin([[1, .5], [1, 2.2]], '#e8899f', 1.6)
    ovale(-6, 7, 5.5, 4.4, '#f9c2d2', 'grain')                               // la tête LARGE
    g.strokeStyle = '#e0607a'; g.lineWidth = 1.6 * u; g.lineCap = 'round'    // LES BRANCHIES
    for (const [x1, y1, x2, y2] of [[-4, 10, -2, 14.5], [-3, 9, .2, 13], [-2.5, 8, 1.8, 11]] as const) {
      g.beginPath(); g.moveTo(X(x1), Y(y1)); g.lineTo(X(x2), Y(y2)); g.stroke()
      g.fillStyle = '#e8798f'; g.beginPath(); g.arc(X(x2), Y(y2), 1.15 * u, 0, 7); g.fill()
    }
    g.strokeStyle = '#c25a72'; g.lineWidth = 1 * u                           // LE SOURIRE
    g.beginPath(); g.moveTo(X(-9.6), Y(5.8)); g.quadraticCurveTo(X(-7), Y(4.2), X(-4.4), Y(5.6)); g.stroke()
    oeil(-7.6, 7.6, .9)
  } else if (kind === 'cameleon') {
    /* LE CAMÉLÉON : queue en SPIRALE, casque pointu, œil en tourelle qui
       regarde EN ARRIÈRE. Sa couleur n'est pas à lui : elle vient de
       `teinteCameleon`, posée par blitPet — c'est la bête qui change de robe
       en marchant, premier cosmétique VIVANT du jeu. */
    const C = (s: number, l: number): string => `hsl(${teinteCameleon},${s}%,${l}%)`
    brin([[6, 6], [11, 4], [13.5, 7], [11.5, 10.5], [9, 8.5], [10.5, 6.8]], C(46, 42), 2.2)  // la spirale
    piece(C(48, 52), 'grain', () => {                                        // le dos voûté
      g.moveTo(X(-7), Y(2)); g.quadraticCurveTo(X(-2), Y(13.5), X(7), Y(4))
      g.lineTo(X(6), Y(1)); g.lineTo(X(-6), Y(1)); g.closePath() })
    for (const [dx, dy] of [[-3.6, 10.2], [-1, 11.2], [1.6, 10]] as const) { // la crête dorsale
      poly([[dx - 1, dy], [dx, dy + 2], [dx + 1, dy]], C(74, 38), 'grain')
    }
    g.fillStyle = C(52, 68)                                                  // le ventre
    g.beginPath(); g.ellipse(X(-1), Y(2.6), 5.5 * u, 1.6 * u, 0, 0, 7); g.fill()
    brin([[-4, .4], [-4, 2.4]], C(46, 40), 1.8); brin([[2.5, .4], [2.5, 2.4]], C(46, 40), 1.8)
    poly([[-4.5, 9.5], [-11.5, 7.5], [-12, 4.5], [-6, 3]], C(50, 56), 'grain')  // la tête casquée
    poly([[-5.5, 9.8], [-3.2, 12.2], [-2.8, 8.8]], C(50, 48), 'grain')       // le casque pointu
    g.strokeStyle = C(40, 34); g.lineWidth = 1 * u                           // la bouche
    g.beginPath(); g.moveTo(X(-11.8), Y(5)); g.lineTo(X(-9), Y(4.3)); g.stroke()
    g.fillStyle = C(52, 70)                                                  // l'œil en tourelle
    g.beginPath(); g.arc(X(-8.4), Y(6.6), 2.5 * u, 0, 7); g.fill()
    oeil(-8, 7.1, .8)                                                        // ... qui regarde derrière
  }
}

/** La robe COURANTE du caméléon (teinte hsl 0-360). Posée par `blitPet` juste
 *  avant de peindre — c'est le seul canal possible sans faire voyager un
 *  paramètre « spécial caméléon » à travers `petPapier`, que pet.ts appelle
 *  sans rien savoir des bêtes. La valeur entre dans la CLÉ DE CACHE de blitPet,
 *  donc deux robes ne se partagent jamais un canvas. */
let teinteCameleon = 120
export function setTeinteCameleon(h: number): void { teinteCameleon = h }

/* ──────────────────────────── 2 · PELUCHE ────────────────────────────
 * Grosse tête (près de la moitié de la bête), grands yeux brillants, tout arrondi, pattes
 * minuscules. C'est la direction la plus LISIBLE à 22 px : une grosse tête ronde et deux yeux
 * survivent à n'importe quelle réduction, là où un museau fin disparaît.
 *
 * ⚠️ Écrit comme un GABARIT partagé et non comme treize dessins séparés : une direction n'en est
 * une que si les treize se ressemblent. Le tableau ci-dessous EST le design ; la fonction ne fait
 * que l'exécuter. Trois bêtes (serpent, araignée, feu follet) n'entrent pas dans le gabarit
 * quadrupède et ont leur bloc, écrit dans le même esprit. */
interface Peluche {
  corps: string; tete: string; motif?: string
  oreille?: 'pointue' | 'ronde' | 'longue' | 'aigrette' | 'aucune'
  queue?: 'fine' | 'panache' | 'plume' | 'aucune'
  iris?: string; museau?: string; bec?: string; accent?: string
}
const PELUCHES: Record<string, Peluche> = {
  chat:    { corps: '#5b5060', tete: '#6b6070', oreille: 'pointue', queue: 'fine', iris: '#8fd06a', museau: '#e8a0b0' },
  canari:  { corps: '#ffd23f', tete: '#ffdf70', motif: 'pois', oreille: 'aucune', queue: 'plume', bec: '#ef7a10', accent: '#ffb02e' },
  loup:    { corps: '#68727e', tete: '#78828e', oreille: 'pointue', queue: 'panache', iris: '#ffc93a', museau: '#cfd6de' },
  perroquet: { corps: '#3aad52', tete: '#46bd5e', oreille: 'aucune', queue: 'plume', bec: '#f7a81e', accent: '#d43c3c' },
  chouette: { corps: '#8a6a48', tete: '#f4ead2', oreille: 'aigrette', queue: 'aucune', iris: '#f0a81e', bec: '#e0821a' },
  renard:  { corps: '#e08040', tete: '#ea8f4e', oreille: 'longue', queue: 'panache', iris: '#ffc93a', museau: '#fdfaf2' },
  taupe:   { corps: '#55424a', tete: '#634e58', oreille: 'aucune', queue: 'fine', museau: '#f0a8b8' },
  'chauve-souris': { corps: '#413255', tete: '#4f3d66', oreille: 'longue', queue: 'aucune', iris: '#ff7a4a' },
}
function petPeluche(k: Kit, kind: string): void {
  const { g, u, X, Y, ovale, poly, brin, oeilVif, piece } = k
  /* Les trois qui n'ont pas de gabarit : leur forme EST leur identité. */
  if (kind === 'serpent') {
    /* ⚠️ REFAIT (31/07) : la première version était un boudin en « C » qui se lisait comme un
       haricot. Ici l'enroulement est explicite — deux anneaux concentriques — et la grosse tête
       est POSÉE DESSUS, pas au bout. */
    ovale(1, 4.5, 10, 5.2, '#4fae52', 'pois')
    ovale(1.5, 4.8, 5.6, 2.8, '#67c96a', 'grain')
    ovale(-1, 12.5, 7, 6.4, '#67c96a', 'pois')                                // LA grosse tête, dessus
    oeilVif(-3.8, 13, 2.6); oeilVif(1.8, 13, 2.6)
    g.strokeStyle = '#2f7a36'; g.lineWidth = 1 * u; g.lineCap = 'round'
    g.beginPath(); g.moveTo(X(-2.6), Y(9.4)); g.quadraticCurveTo(X(-1), Y(8.3), X(.6), Y(9.4)); g.stroke()
    brin([[-1, 8.6], [-1, 6.4]], '#e04a4a', 1.1)
    g.fillStyle = 'rgba(255,150,160,.42)'
    g.beginPath(); g.arc(X(-6.4), Y(10.8), 1.8 * u, 0, 7); g.fill()
    g.beginPath(); g.arc(X(4.4), Y(10.8), 1.8 * u, 0, 7); g.fill()
    return
  }
  if (kind === 'araignee') {
    for (const s of [-1, 1]) for (let j = 0; j < 3; j++)                       // pattes COURTES et rondes
      brin([[s * 3, 6], [s * (7 + j * 2), 8 - j], [s * (9 + j * 2.6), 1]], '#3a2f4a', 2.4)
    ovale(0, 6.5, 6.4, 5.4, '#3a2f4a', 'pois')
    ovale(0, 12, 6, 5.4, '#4a3d5e', 'grain')                                  // tête aussi grosse que le corps
    oeilVif(-2.4, 12.6, 2.2); oeilVif(2.4, 12.6, 2.2)
    g.fillStyle = 'rgba(255,150,160,.4)'
    g.beginPath(); g.arc(X(-5), Y(10.6), 1.5 * u, 0, 7); g.fill()
    g.beginPath(); g.arc(X(5), Y(10.6), 1.5 * u, 0, 7); g.fill()
    return
  }
  if (kind === 'follet') {
    const gl = g.createRadialGradient(X(0), Y(11), 1, X(0), Y(11), 16 * u)
    gl.addColorStop(0, 'rgba(255,250,225,.98)'); gl.addColorStop(.34, 'rgba(255,214,110,.55)')
    gl.addColorStop(1, 'rgba(255,180,60,0)')
    g.fillStyle = gl; g.fillRect(X(-18), Y(29), 36 * u, 36 * u)
    ovale(0, 11, 6.6, 6.6, '#fff3c0', 'grain')                                // une boule joufflue
    oeilVif(-2.5, 11.6, 2.2, '#7a5a10'); oeilVif(2.5, 11.6, 2.2, '#7a5a10')
    g.strokeStyle = '#a87a18'; g.lineWidth = 1.1 * u; g.lineCap = 'round'
    g.beginPath(); g.moveTo(X(-1.4), Y(8.6)); g.quadraticCurveTo(X(0), Y(7.4), X(1.4), Y(8.6)); g.stroke()
    g.fillStyle = 'rgba(255,180,60,.4)'
    g.beginPath(); g.arc(X(-5), Y(9.6), 1.7 * u, 0, 7); g.fill()
    g.beginPath(); g.arc(X(5), Y(9.6), 1.7 * u, 0, 7); g.fill()
    return
  }
  if (kind === 'dragonnet') {
    /* ⚠️ REFAIT (31/07) : passé par le gabarit quadrupède, le dragonnet se lisait comme un CHAT
       violet. Il lui faut ses trois marques : deux cornes, deux ailerons ronds, un museau. */
    for (const sx of [-1, 1]) ovale(sx * 7.5, 11, 3.6, 4.4, '#7d31bd', 'marbre')  // ailerons tout ronds
    ovale(0, 5.5, 6.6, 5, '#9b4ad8', 'marbre')
    brin([[-3.4, .9], [-3.4, 3]], '#7d31bd', 2.6); brin([[3.4, .9], [3.4, 3]], '#7d31bd', 2.6)
    brin([[5, 4], [10, 7], [9, 11]], '#7d31bd', 2.4)
    poly([[-4.6, 16.5], [-3.4, 21], [-1.4, 16.8]], '#e8b8ff', 'grain')            // les cornes
    poly([[1.4, 16.8], [3.4, 21], [4.6, 16.5]], '#e8b8ff', 'grain')
    ovale(0, 12, 7.8, 7, '#ab5ae8', 'marbre')
    g.fillStyle = '#e6c8f6'; g.beginPath(); g.ellipse(X(0), Y(9.4), 3.6 * u, 2.4 * u, 0, 0, 7); g.fill()
    oeilVif(-3, 12.6, 2.7, '#ffd23f'); oeilVif(3, 12.6, 2.7, '#ffd23f')
    g.fillStyle = '#ff9a2e'; g.beginPath(); g.arc(X(-5.6), Y(8.4), 1.5 * u, 0, 7); g.fill()  // flammèche
    g.fillStyle = '#ffe89a'; g.beginPath(); g.arc(X(-8), Y(9), 1 * u, 0, 7); g.fill()
    g.fillStyle = 'rgba(255,150,160,.4)'
    g.beginPath(); g.arc(X(-5.4), Y(10.6), 1.8 * u, 0, 7); g.fill()
    g.beginPath(); g.arc(X(5.4), Y(10.6), 1.8 * u, 0, 7); g.fill()
    return
  }
  if (kind === 'crapaud') {
    /* ⚠️ REFAIT (31/07). Un crapaud n'a pas de tête séparée : ses YEUX SONT SUR LE CRÂNE. Le
       gabarit lui posait une tête ronde par-dessus un corps, ce qui donnait une grenouille en
       peluche générique. Ici, une boule, deux dômes dessus, et un grand sourire. */
    for (const sx of [-1, 1]) ovale(sx * 9, 3, 3.6, 2.8, '#4e7a34', 'pois')       // pattes écartées
    ovale(0, 6.5, 10, 7, '#5c8c3c', 'pois')
    g.fillStyle = '#cfe0a0'; g.beginPath(); g.ellipse(X(0), Y(3.6), 6 * u, 2.8 * u, 0, 0, 7); g.fill()
    g.strokeStyle = '#2b4a1e'; g.lineWidth = 1.5 * u; g.lineCap = 'round'          // LE grand sourire
    g.beginPath(); g.moveTo(X(-7.5), Y(7.4)); g.quadraticCurveTo(X(0), Y(3.4), X(7.5), Y(7.4)); g.stroke()
    for (const dx of [-4.4, 4.4]) ovale(dx, 13.4, 4.6, 4.4, '#6a9c46', 'grain')    // les dômes d'yeux
    oeilVif(-4.4, 13.6, 3, '#f5d92e'); oeilVif(4.4, 13.6, 3, '#f5d92e')
    g.fillStyle = 'rgba(255,150,160,.4)'
    g.beginPath(); g.arc(X(-8.6), Y(9.6), 1.9 * u, 0, 7); g.fill()
    g.beginPath(); g.arc(X(8.6), Y(9.6), 1.9 * u, 0, 7); g.fill()
    return
  }
  const p = PELUCHES[kind]
  if (!p) return
  const mot = p.motif ?? 'grain'
  /* la queue, d'abord : elle passe derrière tout le reste */
  if (p.queue === 'panache') piece(p.corps, mot, () => {
    g.moveTo(X(5), Y(3)); g.quadraticCurveTo(X(16), Y(7), X(11), Y(16))
    g.quadraticCurveTo(X(12), Y(6), X(5), Y(6)); g.closePath() })
  else if (p.queue === 'fine') brin([[5, 3], [11, 7], [10, 12]], p.corps, 2.4)
  else if (p.queue === 'plume') poly([[3, 5], [12, 9], [11, 3]], p.corps, mot)
  ovale(0, 5.5, 7, 5, p.corps, mot)                                           // petit corps bas
  brin([[-4, .8], [-4, 3]], p.corps, 2.6); brin([[3.5, .8], [3.5, 3]], p.corps, 2.6)  // pattes minuscules
  /* les oreilles, sous la tête pour qu'elle les recouvre à la base */
  if (p.oreille === 'pointue') { poly([[-7, 13], [-6, 20], [-1.5, 14]], p.tete, mot); poly([[1.5, 14], [6, 20], [7, 13]], p.tete, mot) }
  else if (p.oreille === 'longue') { poly([[-8, 13], [-8.5, 22], [-2, 14]], p.tete, mot); poly([[2, 14], [8.5, 22], [8, 13]], p.tete, mot) }
  else if (p.oreille === 'ronde') { ovale(-6.5, 17, 3, 3, p.tete, mot); ovale(6.5, 17, 3, 3, p.tete, mot) }
  else if (p.oreille === 'aigrette') { poly([[-6, 16], [-4.5, 21], [-2, 16.5]], p.corps, mot); poly([[2, 16.5], [4.5, 21], [6, 16]], p.corps, mot) }
  ovale(0, 12, 8, 7.2, p.tete, mot)                                           // LA GROSSE TÊTE
  if (p.museau) { g.fillStyle = p.museau; g.beginPath(); g.ellipse(X(0), Y(9.6), 3.4 * u, 2.3 * u, 0, 0, 7); g.fill() }
  oeilVif(-3, 12.4, 2.7, p.iris); oeilVif(3, 12.4, 2.7, p.iris)
  if (p.bec) { g.fillStyle = p.bec; g.beginPath()
    g.moveTo(X(-1.9), Y(9.6)); g.lineTo(X(0), Y(6.6)); g.lineTo(X(1.9), Y(9.6)); g.fill() }
  else { g.strokeStyle = '#2a2230'; g.lineWidth = 1 * u; g.lineCap = 'round'
    g.beginPath(); g.moveTo(X(-1.5), Y(8.8)); g.quadraticCurveTo(X(0), Y(7.6), X(1.5), Y(8.8)); g.stroke() }
  if (p.accent) poly([[-1.4, 19], [.4, 23.5], [2.2, 19]], p.accent, 'grain')   // huppe / crête
  g.fillStyle = 'rgba(255,150,160,.42)'                                        // les joues : la touche peluche
  g.beginPath(); g.arc(X(-5.6), Y(10.4), 1.9 * u, 0, 7); g.fill()
  g.beginPath(); g.arc(X(5.6), Y(10.4), 1.9 * u, 0, 7); g.fill()
}

/* ──────────────────────────── 3 · BAROUDEUR ────────────────────────────
 * Le même animal que la direction FRANC, mais ÉQUIPÉ pour la mine : casque à lampe, écharpe,
 * lunettes remontées, besace. C'est la direction la plus « premium » parce que la plus SPÉCIFIQUE
 * à ce jeu-ci — un chat générique existe partout, un chat casqué de mineur n'existe qu'ici.
 *
 * ⚠️ ELLE PART DE LA SILHOUETTE DE `petFranc`, ET C'EST ASSUMÉ. À 22 px, le nombre de silhouettes
 * réellement distinctes pour un même animal est petit ; inventer une troisième forme aurait donné
 * une bête moins reconnaissable, pas une bête plus riche. Ce qui change, et se voit, c'est
 * l'équipement — un casque modifie franchement la découpe vue de loin. */
function petBaroudeur(k: Kit, kind: string): void {
  const { g, u, X, Y, casque, echarpe, lunettes, besace, poly } = k
  petFranc(k, kind)
  if (kind === 'chat') { casque(-.5, 14.5, .95); echarpe(-1, 8.5, .9, '#c8443a') }
  else if (kind === 'canari') { casque(-.5, 14, .8); besace(2, 7, .85) }
  else if (kind === 'loup') { casque(-6, 16, 1); echarpe(-3, 10.5, 1, '#3a6ec8') }
  else if (kind === 'perroquet') { lunettes(-1, 18.5, .9); besace(2, 8, .9) }
  else if (kind === 'araignee') {                                             // pas de cou : la lampe se porte
    casque(0, 13, .8)
    g.fillStyle = 'rgba(255,236,140,.22)'; g.beginPath(); g.arc(X(0), Y(17.4), 5 * u, 0, 7); g.fill()
  } else if (kind === 'serpent') { casque(-4.5, 19, .8); echarpe(-1, 10.5, .8, '#c8a43a', -1) }
  else if (kind === 'dragonnet') { lunettes(-4, 15.4, .85); besace(3, 6.5, .9) }
  else if (kind === 'chouette') { casque(0, 17.5, 1); besace(3, 8, .9) }
  else if (kind === 'renard') { casque(-6, 15.5, .95); echarpe(-3, 10, .95, '#2f9c6a') }
  else if (kind === 'follet') {                                               // un esprit ne porte rien : il PORTE
    poly([[-3.5, 3], [3.5, 3], [2.6, -1], [-2.6, -1]], '#9a6f3e', 'quadrille') // une petite lanterne dessous
    g.strokeStyle = '#7a5a34'; g.lineWidth = 1 * u
    g.beginPath(); g.moveTo(X(0), Y(5.5)); g.lineTo(X(0), Y(3)); g.stroke()
    g.fillStyle = '#ffe07a'; g.beginPath(); g.arc(X(0), Y(1), 1.4 * u, 0, 7); g.fill()
  } else if (kind === 'taupe') { casque(-4, 11.5, .95); lunettes(-8.5, 8.5, .8) }
  else if (kind === 'chauve-souris') { casque(0, 18, .9); echarpe(0, 12.5, .85, '#c8443a') }
  else if (kind === 'crapaud') { casque(-1.2, 15.2, 1); besace(7.5, 5, .85) }
}

/* ─────────────────────────── LE RÉPARTITEUR ───────────────────────────
 * ⚠️ LA VARIANTE 0 EST LE DESSIN ACTUEL DU JEU, et le paramètre est OPTIONNEL : tant que
 * Claude-Alain n'a pas choisi, `pet.ts` appelle sans variante et rien ne bouge en partie. La
 * planche, elle, demande 1, 2 ou 3. C'est ce qui permet de proposer sans rien casser.
 */
export const VARIANTES_PET = [
  { n: 0, cle: 'actuel', nom: 'En jeu', desc: 'Ce que le jeu peint aujourd’hui — ton choix, s’il est fait.' },
  { n: 1, cle: 'franc', nom: 'Franc', desc: 'La silhouette poussée à fond : aplats nets, un détail signature.' },
  { n: 2, cle: 'peluche', nom: 'Peluche', desc: 'Grosse tête, grands yeux brillants, tout arrondi.' },
  { n: 3, cle: 'baroudeur', nom: 'Baroudeur', desc: 'Le même animal, équipé pour la mine : casque, lampe, écharpe.' },
] as const

/**
 * CE QUE CLAUDE-ALAIN A CHOISI (31/07), après la planche.
 *
 * ⚠️ C'est LA table qui décide de ce que le jeu peint. Une bête absente d'ici garde son dessin
 * d'origine — c'est le cas du canari, qu'il a explicitement voulu garder tel quel, et des trois
 * qui attendent un second passage.
 */
export const CHOIX_PET: Record<string, number> = {
  chat: 3,            // Baroudeur
  loup: 1,            // Franc
  perroquet: 1,       // Franc
  araignee: 3,        // Baroudeur
  chouette: 1,        // Franc
  renard: 1,          // Franc
  follet: 1,          // Franc
  'chauve-souris': 1, // Franc
  taupe: 3,           // Baroudeur
  serpent: 2,         // Peluche (second tour)
  crapaud: 1,         // Franc (second tour)
  dragonnet: 1,       // Franc (troisieme tour) - refait d'apres une vraie anatomie, et double
  // canari : « Actuel » - il a garde le dessin d'origine, donc pas d'entree. C'est le SEUL des
  // treize a garder son dessin de depart, et c'est une decision, pas un oubli.
  // -- Vague du 05/08, EN ATTENTE de validation sur /pages/cosmetiques2.html.
  // Dessinées directement en Franc : sans entrée ici, variante 0 tomberait sur
  // petLegacy qui ne les connaît pas, et la bête serait INVISIBLE (le piège du
  // Casque ailé, version familier).
  herisson: 1,
  golem: 1,
  axolotl: 1,
  cameleon: 1,
}

/** Les betes encore a trancher : la planche les remonte en tete.
 *  VIDE depuis le 31/07 : les treize sont tranchees. */
export const PETS_EN_ATTENTE: readonly string[] = []

/**
 * ⚠️ LA VARIANTE 0 VEUT DIRE « CE QUE LE JEU PEINT AUJOURD'HUI », pas « le dessin d'origine ».
 * Elle passe donc par CHOIX_PET. C'est ce qui fait que `pet.ts` n'a rien à savoir de tout ceci
 * (il appelle sans variante), et que la vignette « Actuel » de la planche montre toujours la
 * vérité du jeu plutôt qu'un état périmé.
 */
export function petPapier(g: Ctx, cx: number, base: number, T: number, kind: string, variante = 0): void {
  const v = variante === 0 ? (CHOIX_PET[kind] ?? 0) : variante
  if (v === 0) { petLegacy(g, cx, base, T, kind); return }
  const variante_ = v
  const k = faireKit(g, cx, base, T)
  if (variante_ === 1) petFranc(k, kind)
  else if (variante_ === 2) petPeluche(k, kind)
  else petBaroudeur(k, kind)
}

/* ----------------------------------------------------------------- le cache */
const CACHE = new Map<string, HTMLCanvasElement>()
/* 100 et non 60 : le caméléon seul consomme 18 entrées par taille (une robe par
   cran de teinte) ; à 60, il évinçait les douze autres bêtes du cache. */
const CACHE_MAX = 100
/* la queue du renard et les pattes de l'araignée sortent largement de la boîte */
const MARGE = 0.9

/* ═══════════════════ L'ALLURE : ce qui fait qu'un compagnon MARCHE ═══════════════════
 * Signalé par Claude-Alain (31/07) : « il faut que les pets soient animés, il y en a certains qui
 * glissent sur le sol ». Exact, et pour une raison bête : `pet.ts` CALCULE une frame (trot 0-1,
 * repos 2, spéciale 3) puis appelle `blitPet` SANS LA LUI PASSER. Depuis le passage au papier
 * découpé du 28/07, les treize compagnons étaient des images fixes qu'on translatait — donc qui
 * glissaient.
 *
 * Les silhouettes sont peintes d'une pièce : il n'y a pas de patte séparée à faire pivoter. On
 * anime donc comme s'anime une marionnette de papier — la pièce entière BASCULE et REBONDIT
 * autour de ses pieds. Deux poses alternées suffisent à lire une course ; c'est le principe de
 * toute animation à deux images.
 *
 * ⚠️ Appliqué au TIRAGE, pas au cache : sinon il faudrait quatre dessins par bête et par taille.
 */
// ⚠️ CES QUATRE POSES NE SERVENT PLUS AU JEU depuis le 11/08 : `pet.ts` calcule désormais une pose
// CONTINUE (client/pet-allure.ts) et la passe à `blitPet`. Elles restent la valeur par défaut des
// autres appelants — vestiaire, planche, carte de récompense — qui montrent une bête posée et n'ont
// pas d'état d'animation à fournir.
const ALLURES: PosePet[] = [
  { angle: -0.075, sx: 1.03, sy: 0.97, dy: 0, skew: 0 },   // 0 · appui avant : la bête plonge en avant
  { angle: 0.075, sx: 0.98, sy: 1.03, dy: -2, skew: 0 },   // 1 · détente : elle se redresse et décolle
  { angle: 0, sx: 1.02, sy: 0.98, dy: 0, skew: 0 },        // 2 · repos : à peine écrasée, elle respire
  { angle: -0.03, sx: 0.96, sy: 1.06, dy: -1, skew: 0 },   // 3 · spéciale : étirée vers le haut
]

/**
 * Peint un compagnon dans une case size×size au coin (0,0) du contexte courant.
 * Le contexte est supposé déjà translaté/miroité par l'appelant.
 *
 * ⚠️ LES SILHOUETTES SONT DESSINÉES TOURNÉES VERS LA GAUCHE (museau du loup à -16, bec du canari
 * à -6, crâne du dragonnet à -9...), alors que `pet.ts` applique son miroir en supposant l'inverse
 * (`mirror = st.face < 0`). Résultat : les sept bêtes ASYMÉTRIQUES marchaient à reculons, et les
 * six symétriques ne montraient rien — d'où le « certains » du signalement.
 * On retourne donc ici, une fois pour toutes : la SORTIE de `blitPet` regarde vers la droite, ce
 * que le reste du jeu attend. Corriger côté `pet.ts` aurait mis une exception « sauf les pets »
 * dans une règle d'orientation partagée avec tout le rendu.
 */
export function blitPet(ctx: Ctx, kind: string, size: number, variante = 0, frame = 0, pose?: PosePet): void {
  const T = Math.max(6, Math.round(size))
  // le jeu nomme ses compagnons « pet-chat » ; les silhouettes s'appellent « chat »
  const nom = kind.replace(/^pet-/, '')
  /* LE CAMÉLÉON CHANGE DE ROBE EN MARCHANT : sa teinte glisse d'un cran toutes
     les 500 ms (18 crans, un tour en 9 s). L'horloge murale est PERMISE ici :
     on est côté rendu, jamais dans la sim, et une teinte continue ne scintille
     pas — l'interdit de particles.ts vise l'aléa par image, pas le temps.
     Chaque cran est un canvas de cache À PART (la phase entre dans la clé) :
     teinter un canvas partagé aurait déteint toutes les autres bêtes. */
  const phase = nom === 'cameleon' ? Math.floor(performance.now() / 500) % 18 : 0
  if (nom === 'cameleon') setTeinteCameleon(phase * 20)
  // ⚠️ La variante entre dans la CLÉ DU CACHE : sans elle, la planche afficherait quatre fois le
  // même dessin, le premier peint gagnant pour toutes les autres.
  const cle = nom + '|' + T + '|' + variante + (nom === 'cameleon' ? '|h' + phase : '')
  let cv = CACHE.get(cle)
  if (!cv) {
    const w = Math.ceil(T * (1 + MARGE * 2)), h = Math.ceil(T * (1 + MARGE))
    cv = document.createElement('canvas')
    cv.width = w; cv.height = h
    const g = cv.getContext('2d')
    if (g) petPapier(g, w / 2, T * (1 + MARGE * 0.5), T, nom, variante)
    if (CACHE.size >= CACHE_MAX) { const k = CACHE.keys().next().value; if (k) CACHE.delete(k) }
    CACHE.set(cle, cv)
  }
  const a = pose ?? ALLURES[frame] ?? ALLURES[0]!
  ctx.save()
  // Lissage ACTIF : ces dessins sont vectoriels, pas du pixel art. `pet.ts` le coupe pour la
  // vieille feuille de sprites ; sans le rétablir, la bascule d'allure crénellerait les bords.
  ctx.imageSmoothingEnabled = true
  // Pivot aux PIEDS, au milieu : une bascule autour du centre ferait tourner la bête sur elle-même.
  ctx.translate(size / 2, size)
  ctx.rotate(a.angle)
  // CISAILLEMENT (11/08) : le haut du corps traîne derrière les pattes, puis les rattrape. C'est le
  // « follow through » de l'animation traditionnelle, et à cette taille c'est lui qui donne le
  // poids — bien plus qu'un détail de patte qu'on ne verrait pas.
  if (a.skew) ctx.transform(1, 0, a.skew, 1, 0, 0)
  ctx.scale(a.sx * -1, a.sy)          // le -1 : les silhouettes regardent à gauche, le jeu à droite
  ctx.translate(-size / 2, -size + a.dy)
  ctx.drawImage(cv, size / 2 - cv.width / 2, size - T * (1 + MARGE * 0.5))
  ctx.restore()
}
